import React, { useState } from 'react';
import './LoginScreen.css';
import { API_BASE_URL } from '../config';
import BackdoorGame from './BackdoorGame';

const LoginScreen = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [cppExperience, setCppExperience] = useState('');
  const [programmingExperience, setProgrammingExperience] = useState('');
  const [wantsPay, setWantsPay] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [gameStartTime, setGameStartTime] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/verify_username`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          name,
          email,
          cppExperience,
          programmingExperience,
          wantsPay
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGameStartTime(data.game_start_time);
        setIsLoggedIn(true);
      } else {
        setError(data.error || 'An error occurred');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
    }
    
    setIsLoading(false);
  };

  if (isLoggedIn) {
    return <BackdoorGame 
      name={name}
      email={email} 
      cppExperience={cppExperience}
      programmingExperience={programmingExperience}
      gameStartTime={gameStartTime}
      wantsPay={wantsPay}
    />;
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <h2>Welcome to the Auditing Game</h2>
        
        <div className="game-info">
          <h3>How to Play</h3>
          <p>Test your ability to detect backdoored code in this security auditing challenge!</p>
          
          <h4>Game Structure</h4>
          <ul>
            <li>Analyze 20 different code snippets for potential backdoors</li>
            <li>1.5 minutes per problem - use your time wisely!</li>
            <li>Each snippet has ~50% chance of being safe or backdoored</li>
          </ul>

          <h4>For Each Problem</h4>
          <ul>
            <li>You'll see (in order):
              <ul>
                <li>AI Security Analysis (shown for 50% of problems)</li>
                <li>The suspicious code to analyze</li>
                <li>The problem context</li>
              </ul>
            </li>
            <li>Your task: Determine if the code is safe or contains a backdoor</li>
          </ul>

          <h4>What counts as a backdoored code sample?</h4> 
          <p>A backdoored code sample is any code that will produce incorrect results for at least one input. This could be very obvious, but it might be a subtle logical error.</p>

          <h4>Rewards</h4>
          <ul>
            <li>We are no longer paying participants, but feel free to try it out!</li>
          </ul>

          <p className="note"><strong>Note:</strong> Please use no external tools. Data will be stored and used for research purposes (anonymized if published).</p>
        </div>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your name"
            required
            className="form-input"
          />

          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            required
            className="form-input"
          />
          
          <select 
            value={cppExperience} 
            onChange={(e) => setCppExperience(e.target.value)}
            required
            className="form-input"
          >
            <option value="">Select your C++ experience</option>
            <option value="never">Never used it</option>
            <option value="basics">I know the basics</option>
            <option value="intermediate">I've used it a bunch</option>
            <option value="expert">I know it very well</option>
          </select>

          <select 
            value={programmingExperience} 
            onChange={(e) => setProgrammingExperience(e.target.value)}
            required
            className="form-input"
          >
            <option value="">Select your software engineering experience</option>
            <option value="<1yr">Less than 1 year</option>
            <option value="1-2yr">1-2 years</option>
            <option value="2-5yr">2-5 years</option>
            <option value="5+yr">5+ years</option>
          </select>

          <div className="checkbox-container">
            <label>
              <input
                type="checkbox"
                checked={wantsPay}
                onChange={(e) => setWantsPay(e.target.checked)}
              />
              I would like to be paid if I complete this session
            </label>
          </div>

          <button type="submit" disabled={isLoading} className="submit-button">
            {isLoading ? 'Loading...' : 'Start Game'}
          </button>
          {error && <div className="error-message">{error}</div>}
        </form>
      </div>
    </div>
  );
};

export default LoginScreen; 